import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 text-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">
            ← Back to App
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-8">About backtime.live</h1>

        <div className="prose prose-invert max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">What is backtime.live?</h2>
            <p className="text-gray-300">
              backtime.live is a service timing application designed to help organizations coordinate and
              manage their event schedules with precision. Whether you're running worship services,
              conferences, broadcasts, or any time-sensitive event, backtime.live keeps everyone synchronized.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">Key Features</h2>

            <div className="grid gap-4 mt-4">
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-400 mb-2">Real-Time Synchronization</h3>
                <p className="text-gray-300">
                  All connected devices stay perfectly in sync with server time, ensuring everyone
                  sees the same countdown regardless of their device's local clock.
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-400 mb-2">Flexible Scheduling</h3>
                <p className="text-gray-300">
                  Create recurring schedules with multiple start times per day. Perfect for
                  organizations with regular weekly events.
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-400 mb-2">Element-Based Structure</h3>
                <p className="text-gray-300">
                  Organize your event into elements with anchor-based timing. Set fixed moments
                  (like message start) and let other elements chain around them.
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-400 mb-2">Public Sharing</h3>
                <p className="text-gray-300">
                  Share your session with team members via QR code or link. Viewers can follow
                  along without needing an account.
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-400 mb-2">Cross-Platform</h3>
                <p className="text-gray-300">
                  Access backtime.live from any device with a web browser, or use the native iOS app
                  for the best mobile experience.
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-400 mb-2">Time Offset Adjustments</h3>
                <p className="text-gray-300">
                  Running ahead or behind? Adjust the offset on the fly to keep your countdown
                  accurate to real-world conditions.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">How It Works</h2>
            <ol className="list-decimal pl-6 text-gray-300 mt-2 space-y-3">
              <li>
                <strong>Create a Session:</strong> Set up your event with a name, schedule, and timezone.
              </li>
              <li>
                <strong>Define Elements:</strong> Add time elements with anchored moments (fixed times)
                and chained segments that flow around them.
              </li>
              <li>
                <strong>Share with Your Team:</strong> Make your session public and share the QR code or
                link with anyone who needs to follow along.
              </li>
              <li>
                <strong>Stay in Sync:</strong> Everyone sees the same real-time countdown, automatically
                advancing through elements as time progresses.
              </li>
            </ol>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">Use Cases</h2>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-2">
              <li><strong>Houses of Worship:</strong> Coordinate service elements, countdowns to message start, worship sets</li>
              <li><strong>Broadcast Productions:</strong> Keep crew synchronized on show timing and segment transitions</li>
              <li><strong>Conferences:</strong> Manage session timing, breaks, and speaker transitions</li>
              <li><strong>Live Events:</strong> Coordinate backstage crew with front-of-house timing</li>
              <li><strong>Meetings:</strong> Keep presentations and discussions on schedule</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">Version Information</h2>
            <p className="text-gray-300">
              {process.env.REACT_APP_GIT_COMMIT ? (
                <>
                  Current version: {process.env.REACT_APP_GIT_COMMIT}
                  {process.env.REACT_APP_GIT_BRANCH && ` (${process.env.REACT_APP_GIT_BRANCH})`}
                </>
              ) : (
                'Version information not available'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">Contact & Support</h2>
            <p className="text-gray-300">
              For questions, feedback, or support inquiries, please contact us at support@backtime.live.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-gray-700 text-center text-sm text-gray-500">
          <Link to="/terms" className="hover:text-gray-300 mr-4">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-gray-300">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
